/**
 * "zmieniony 2 dni temu" — but only the half that is a DECISION.
 *
 * Two things happen when a timestamp becomes that sentence, and they belong in different places.
 * Choosing the UNIT is a judgement about what a trainer needs to know: eleven days ago is more
 * legible as "2 tyg. temu" than as "11 dni temu", and four minutes ago is "dzisiaj" rather than a
 * number nobody asked for. Rendering the chosen unit in Polish or English is `Intl`'s job, and the
 * screen's.
 *
 * So this module answers with a unit and a count, and the caller formats. That is what makes the
 * rule testable at a boundary — the day the week threshold moves, one test moves with it — and it is
 * why `now` is a PARAMETER rather than a `Date.now()` inside: a rule that reads the clock cannot be
 * tested at midnight, at a month end, or on the day the answer flips.
 *
 * ── Calendar days, not elapsed hours ─────────────────────────────────────────────────────────
 * A plan edited at 23:50 yesterday was edited YESTERDAY, whatever the elapsed hours say, and a
 * trainer reading the library at 00:10 knows that. So the difference is measured between local
 * midnights rather than between instants: `Intl.RelativeTimeFormat` with `numeric: 'auto'` then
 * turns 1 into "wczoraj" and 0 into "dzisiaj" for free, which is exactly the wording a person uses.
 *
 * Local midnight, deliberately, and it is the one place this module is opinionated about time zones:
 * the timestamp is UTC and the trainer is not. Comparing UTC days would tell a trainer in Warsaw who
 * edited a plan at 00:30 that they did it yesterday.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

/** The unit a freshness line is worded in — the three `Intl.RelativeTimeFormat` units used here. */
export type FreshnessUnit = 'day' | 'week' | 'month';

/**
 * How long ago, already reduced to a unit and a whole count.
 *
 * `value` is POSITIVE — "two weeks ago" is `{ unit: 'week', value: 2 }`. The minus sign belongs to
 * the formatter, and putting it here would mean every caller remembered to negate.
 */
export interface Freshness {
  readonly unit: FreshnessUnit;
  readonly value: number;
}

const MS_PER_DAY = 86_400_000;

/**
 * Where each unit takes over.
 *
 * · Under a week, days — "5 dni temu" is a number a trainer holds in their head.
 * · Under roughly two months, weeks. The canon's own longest example is "4 tyg. temu", and beyond
 *   about eight weeks the week count stops being a quantity and becomes arithmetic.
 * · Beyond that, months. Nothing counts in years: a library where the oldest plan is "14 mies. temu"
 *   is more informative than one where it is "1 rok temu", and the column is 124px wide either way.
 */
const DAYS_IN_WEEK = 7;
const DAYS_BEFORE_MONTHS = 56;
const DAYS_PER_MONTH = 30;

/** Local midnight of the day an instant falls on. See the header for why local rather than UTC. */
function startOfLocalDay(at: Date): number {
  return new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime();
}

/**
 * How long ago a timestamp was, or `null` when there is nothing to say.
 *
 * `null` covers three cases the caller treats identically and must not treat as "just now": no
 * timestamp at all (a plan nobody has edited), a string `Date` cannot parse, and a timestamp in the
 * FUTURE. The last one is not defensive — a clock skew of a few seconds between the server's stamp
 * and the browser is ordinary, and "za 3 sekundy" under a column headed ŚWIEŻOŚĆ reads as a bug.
 * Same-day edits are `{ unit: 'day', value: 0 }`, which the formatter renders as "dzisiaj".
 */
export function freshnessOf(iso: string | null, now: Date): Freshness | null {
  if (iso === null) return null;
  const at = new Date(iso);
  const time = at.getTime();
  if (Number.isNaN(time)) return null;

  const days = Math.round((startOfLocalDay(now) - startOfLocalDay(at)) / MS_PER_DAY);
  if (days < 0) return null;
  if (days < DAYS_IN_WEEK) return { unit: 'day', value: days };
  if (days < DAYS_BEFORE_MONTHS) return { unit: 'week', value: Math.floor(days / DAYS_IN_WEEK) };
  return { unit: 'month', value: Math.floor(days / DAYS_PER_MONTH) };
}
